import React, { useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useFormikContext } from 'formik'
import VideocamIcon from '@material-ui/icons/Videocam'
import eventFormCourtLogosStyles from './eventFormCourtLogos.styles'
import volleyballCourt from '../../assets/images/volleyball_court.svg'
import ImageCropWithEdit from '../ui/ImageCropComponents/ImageCropWithEdit/ImageCropWithEdit'
import ConditionalRender from '../ConditionalRender/ConditionalRender'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../utils/constants/gameEventFormBroadcastFields'
import CourtLogosPreviewEditBox from '../CourtLogosPreviewEditBox/CourtLogosPreviewEditBox'
import CROPPER_SHAPES from '../../utils/constants/cropperShapes'
import { isBroadcastEnded } from '../../utils/helpers/isFieldDisabled'
import MyUploadFilesField from '../form/fields/MyUploadFilesField/MyUploadFilesField'
import ORGANISATION_SPORTS from '../../utils/constants/organisationSports'
import { STREAM_FEATURES, supports } from '../../utils/constants/streamTypeCapabilities'

// Volleyball keeps its existing image-based court + logo positions.
const VOLLEYBALL_LOGO_POSITIONS = {
  topLeft: { top: '24.5%', left: '15%', width: '18%', height: '18%' },
  topRight: { top: '24%', right: '15%', width: '18%', height: '18%' },
  bottomLeft: { bottom: '16.5%', left: '15%', width: '18%', height: '18%' },
  bottomRight: { bottom: '16.5%', right: '15%', width: '18%', height: '18%' },
}

// Full-court line-art matching the design (viewBox 600x360): rounded outer rect, center circle,
// 3-point arcs, painted keys with dashed free-throw semicircles, backboards and hoops.
const BasketballCourt = ({ className }) => (
  <svg
    className={className}
    viewBox='20 20 560 320'
    fill='none'
    stroke='#1A1A1A'
    strokeWidth='2.4'
    strokeLinejoin='round'
    preserveAspectRatio='xMidYMid meet'
  >
    <rect x='24' y='24' width='552' height='312' rx='8' strokeWidth='2.8' />
    <line x1='300' y1='24' x2='300' y2='336' />
    <circle cx='300' cy='180' r='38' />
    {/* left half */}
    <path d='M24,36 L66,36 A144,144 0 0 1 66,324 L24,324' />
    <rect x='24' y='128' width='126' height='104' />
    <path d='M150,142 A38,38 0 0 1 150,218' />
    <path d='M150,142 A38,38 0 0 0 150,218' strokeDasharray='6 6' />
    <line x1='40' y1='152' x2='40' y2='208' strokeWidth='2.8' />
    <circle cx='52' cy='180' r='11' />
    {/* right half */}
    <path d='M576,36 L534,36 A144,144 0 0 0 534,324 L576,324' />
    <rect x='450' y='128' width='126' height='104' />
    <path d='M450,142 A38,38 0 0 0 450,218' />
    <path d='M450,142 A38,38 0 0 1 450,218' strokeDasharray='6 6' />
    <line x1='560' y1='152' x2='560' y2='208' strokeWidth='2.8' />
    <circle cx='548' cy='180' r='11' />
  </svg>
)

BasketballCourt.propTypes = { className: PropTypes.string }

const EventFormCourtLogos = ({ state, setMediaIsUploading, selectedEventType, sport }) => {
  const isBasketball = sport === ORGANISATION_SPORTS.BASKETBALL
  const { values, setFieldValue } = useFormikContext()
  const useStyles = useMemo(() => makeStyles(eventFormCourtLogosStyles(VOLLEYBALL_LOGO_POSITIONS)), [])
  const classes = useStyles()

  const setCroppedImage = useCallback((name, value) => setFieldValue(name, value), [setFieldValue])

  const isLivestreamTypeFull = useMemo(
    () =>
      (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
        values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
      supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.COURT_LOGOS),
    [
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
    ],
  )

  const isBroadcastEndedMemo = useMemo(() => isBroadcastEnded(state), [state])

  // Each logo maps a form field to its box class per sport (new design for basketball,
  // legacy positions for volleyball).
  const logo = useCallback(
    (field, basketballPos, volleyballClass) => {
      const isCenter = field === GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO
      return (
        <ImageCropWithEdit
          defaultImageSrc={values[field]}
          setCroppedImage={(croppedImage) => setCroppedImage(field, croppedImage)}
          aspect={isCenter ? 1 : 4 / 3}
          minZoom={0.2}
          cropShape={isCenter ? CROPPER_SHAPES.ROUND : CROPPER_SHAPES.RECT}
          dialogBottomText='Proposed Width: 300-1500 pixels'
          isDisabled={isBroadcastEndedMemo}
          EditPreviewComponent={CourtLogosPreviewEditBox}
          editPreviewComponentClassName={
            isBasketball
              ? classnames(isCenter ? classes.logoBoxCenter : classes.logoBox, classes[basketballPos])
              : volleyballClass
          }
        />
      )
    },
    [values, setCroppedImage, isBroadcastEndedMemo, isBasketball, classes],
  )

  return (
    <ConditionalRender renderValue={isLivestreamTypeFull}>
      <div className={classes.wrapper}>
        {supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.SPONSOR_VIDEOS) &&
          selectedEventType?.code === 'game' && (
            <MyUploadFilesField
              label='Upload MP4 Video'
              maxVideoNum={6}
              acceptedTypes='.mp4'
              setMediaIsUploading={setMediaIsUploading}
            />
          )}

        {isBasketball ? (
          <div className={classes.courtOuter}>
            <div className={classes.courtSvgWrapper}>
              <BasketballCourt className={classes.courtSvg} />
              {logo(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO, 'posTopLeft')}
              {logo(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO, 'posBottomLeft')}
              {logo(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO, 'posCenter')}
              {logo(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO, 'posTopRight')}
              {logo(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO, 'posBottomRight')}
            </div>
            <div className={classes.cameraMarker} title='Camera position'>
              <VideocamIcon className={classes.cameraIcon} />
            </div>
          </div>
        ) : (
          <div className={classes.courtWrapper}>
            <img className={classes.courtImg} src={volleyballCourt} alt='court' />
            {logo(
              GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO,
              'posTopLeft',
              classes.editBoxTopLeft,
            )}
            {logo(
              GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO,
              'posTopRight',
              classes.editBoxTopRight,
            )}
            {logo(
              GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO,
              'posBottomLeft',
              classes.editBoxBottomLeft,
            )}
            {logo(
              GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO,
              'posBottomRight',
              classes.editBoxBottomRight,
            )}
          </div>
        )}
      </div>
    </ConditionalRender>
  )
}

EventFormCourtLogos.defaultProps = {
  sport: ORGANISATION_SPORTS.BASKETBALL,
}

EventFormCourtLogos.propTypes = {
  state: PropTypes.string,
  selectedEventType: PropTypes.oneOfType([PropTypes.object]),
  setMediaIsUploading: PropTypes.func,
  sport: PropTypes.oneOf(Object.values(ORGANISATION_SPORTS)),
}

export default EventFormCourtLogos
